import { type ComponentType, Suspense, lazy } from 'react'

import { useDesignMode } from 'src/design-mode/useDesignMode'

/**
 * The app-wide toast host, mounted once at the app root (`src/App/index.tsx`).
 *
 * It renders NOTHING at all on the antd rendering — "Old design", the shipped
 * default — which is what keeps that path byte-identical: antd's own notice
 * holder is still the only thing on the page, created by antd the first time a
 * `message.*` call reaches it, exactly as before this seam existed.
 *
 * On the shadcn rendering it mounts Sonner's host through `React.lazy`, so the
 * engine is fetched once, at app boot, rather than on the first toast — a
 * chunk fetched at the moment an error needs reporting is a chunk that can
 * fail when the error is a network error. `../Toaster` and `./sonner/engine`
 * together are the whole of the "download it only if you use it" property; see
 * that file's header for what happens to a toast fired while it is in flight.
 *
 * Mounted OUTSIDE `AntdI18n`'s `<ConfigProvider>` on purpose: Sonner reads no
 * antd context, and the app root is where the other two markup-less globals
 * behind this toggle already sit (`AppFont`, `AntdSpinDefaultIndicator`).
 */
const SonnerToaster = lazy(
  (): Promise<{ default: ComponentType }> =>
    // The `catch` is the degradation, not dead error handling: the nearest
    // boundary above this host is the ROOT `ErrorBoundary` (`src/index.tsx`), so
    // a rejected chunk — offline, blocked, anything the `vite:preloadError`
    // reload does not cover — would take the whole app to the "Oops" screen
    // rather than costing the user a nicer-looking toast. Rendering nothing is
    // precisely the state `./index.ts` already falls through to antd from, since
    // `getSonnerToast()` simply stays `null`.
    import('./sonner/SonnerToaster').catch(() => ({ default: () => null }))
)

export const Toaster = () => {
  const mode = useDesignMode()
  if (mode !== 'shadcn') return null
  return (
    <Suspense fallback={null}>
      <SonnerToaster />
    </Suspense>
  )
}
